import { useEffect, useState } from 'react';
import { api, datum } from '../api.ts';
import { useVerzoegert } from './Schluesselsuche.tsx';

/**
 * Wo eine Rechnung ist, egal in welchem Schritt man gerade steht.
 *
 * ORIGINALTEXT MARVIN (19.10.2026, zu RE1307 und RE1308 von AL Dienstleistungen)
 *   „Kannst du hier bitte eine Suchfunktion einbauen, dass ich auch nach
 *    Rechnungsnummern suchen kann, wenn irgendwas nicht stimmt? Und dann
 *    fehlen, wenn Rechnungsnummern nicht importiert wurden, was mache ich
 *    denn dann?"
 *
 * BEFUND: beide Rechnungen waren längst im System (22.09. aus dem Postfach,
 * Auftrag zugeordnet, Belege-Ordner, fällig 17.10., unbezahlt). Sie standen
 * nur nicht in Schritt 2, weil dort nur der Prüfordner liegt; die Suche der
 * Seite filterte den sichtbaren Schritt und sagte sonst nichts. **Eine
 * Suche, die „nichts" sagt, obwohl es die Rechnung gibt, ist eine falsche
 * Antwort.** Dieser Kasten fragt bei jeder Eingabe über ALLE Rechnungen
 * (`GET /rechnungen?zustand=alle&suche=`, derselbe Weg wie „Alle Rechnungen
 * der Subunternehmer") und sagt zu jedem Treffer, wo er liegt und in welchem
 * Schritt er dran ist.
 */
export function Rechnungsfinder({ suche, springe, verborgen, pruefe }: {
  suche: string;
  springe: (wo: 'schritt1' | 'schritt2' | 'schritt3' | 'alle') => void;
  /** Öffnet das Prüffenster zu einer Rechnung, egal in welchem Schritt sie liegt (.408). */
  pruefe?: (id: string) => void;
  /** Rechnungsnummern, die im sichtbaren Schritt schon stehen: die nennt der Kasten nicht noch einmal. */
  verborgen?: string[];
}) {
  const s = useVerzoegert(suche.trim());
  const [d, setD] = useState<{ fuer: string; zeilen: any[] } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let lebt = true;
    if (s.length < 3) { setD(null); return; }
    api.rechnungen('alle', s)
      .then((r: any) => { if (lebt) { setD({ fuer: s, zeilen: r?.zeilen ?? r ?? [] }); setFehler(null); } })
      .catch((f: any) => { if (lebt) setFehler(String(f?.message ?? f)); });
    return () => { lebt = false; };
  }, [s]);

  if (s.length < 3) return null;
  if (fehler) return <div className="hinweis warnung">Suche über alle Rechnungen: {fehler}</div>;
  if (!d || d.fuer !== s) return null;
  const zeilen = d.zeilen.filter((z: any) => !(verborgen ?? []).includes(String(z.rechnungsnummer ?? '')));
  if (!zeilen.length) {
    return (
      <div className="hinweis">
        Zu „{s}" gibt es in keinem Schritt eine Rechnung, auch nicht bezahlt oder abgelehnt. Kommt sie aus
        dem Postfach, steht sie nach dem nächsten Lauf hier; sonst unter „Alle Rechnungen der Subunternehmer"
        von Hand erfassen.
      </div>
    );
  }
  return (
    <div className="karte" style={{ marginBottom: 12 }}>
      <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <b>Zu „{s}" über alle Rechnungen: {zeilen.length} {zeilen.length === 1 ? 'Treffer' : 'Treffer'}</b>
        <button type="button" className="knopf klein" onClick={() => springe('alle')}>Alle Rechnungen anzeigen</button>
      </div>
      <table className="liste" style={{ marginTop: 8 }}>
        <thead>
          <tr><th>Rechnung</th><th>Firma</th><th>Datum</th><th>Auftrag</th><th>Wo sie liegt</th><th>Dran in</th><th></th></tr>
        </thead>
        <tbody>
          {zeilen.map((z: any) => {
            const l = lage(z);
            return (
              <tr key={z.id}>
                <td className="kennung">{z.rechnungsnummer ?? <span className="leise">ohne Nummer</span>}</td>
                <td>{z.firma ?? ''}</td>
                <td>{z.rechnungsdatum ? datum(z.rechnungsdatum) : ''}{z.eingang_am ? <div className="klein leise">eingegangen {datum(z.eingang_am)}</div> : null}</td>
                <td>{anschrift(z)}</td>
                <td>{l.wo}</td>
                <td>
                  {l.schritt
                    ? <button type="button" className="knopf klein" onClick={() => springe(l.schritt!)}>{l.schrittText}</button>
                    : <span className="leise">{l.schrittText}</span>}
                </td>
                <td>{pruefe && <button type="button" className="knopf klein" onClick={() => pruefe(z.id)}>Prüfen →</button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function anschrift(z: any): string {
  const a = [z.strasse, [z.hausnummer, z.hausnummer_zusatz].filter(Boolean).join('')].filter(Boolean).join(' ');
  const teile = [a, z.ort_text].filter(Boolean).join(', ');
  const kls = z.kls_id ? `KLS ${z.kls_id}` : '';
  return [teile, kls].filter(Boolean).join(', ') || 'kein Auftrag zugeordnet';
}

/** Ein Satz, wo die Rechnung liegt, und der Schritt, in dem sie dran ist. */
function lage(z: any): { wo: string; schritt: 'schritt2' | 'schritt3' | null; schrittText: string } {
  const ablage = String(z.ablage ?? '');
  const ablageText = ablage === 'pruefung' ? 'Prüfordner'
    : ablage === 'freigegeben' ? 'freigegeben, noch im Prüfordner'
    : ablage === 'abgelegt' ? 'Belege-Ordner'
    : ablage === 'direkt' ? 'Belege-Ordner (direkt, vor der Prüfordner-Regel)'
    : ablage || 'unbekannt';
  if (z.bezahlt_am) return { wo: `${ablageText}, bezahlt ${datum(z.bezahlt_am)}`, schritt: null, schrittText: 'erledigt' };
  if (z.abgelehnt_grund) return { wo: `${ablageText}, abgelehnt: ${z.abgelehnt_grund}`, schritt: null, schrittText: 'abgelehnt' };
  if (z.zahlung_zustand && z.zahlung_zustand !== 'abgebrochen') return { wo: `${ablageText}, in Revolut zur Freigabe`, schritt: 'schritt3', schrittText: 'Schritt 3, Zahlung' };
  if (ablage === 'pruefung' && !z.freigegeben_am) return { wo: ablageText + (z.geprueft ? ', geprüft, wartet' : ''), schritt: 'schritt2', schrittText: 'Schritt 2, Prüfen' };
  const faellig = z.faellig_am ? `, fällig ${datum(z.faellig_am)}` : '';
  return { wo: ablageText + faellig, schritt: 'schritt3', schrittText: 'Schritt 3, Zahlung' };
}
